import { BetStatusType } from '@williamhill/rosi-ui-shared/src/types';
import { useEffect, useMemo, useState } from 'react';
import { queryAndUpdateTickets } from '../services';
import ticketStore from './ticket-store';
import { TicketView, useServices } from '../services';
import { getLogger } from '@williamhill/rosi-ui-shared/src/lib/logger-plus';

const logger = getLogger('bet-tracker/bets/use-bets');

export function useBets() {
  const { dynamicConfig, americanWageringGateway, loading: loadingServices } = useServices();
  const [tickets, setTickets] = useState<TicketView[]>(ticketStore.getAll().sort(compareTickets));
  const filteredTickets = useMemo(() => {
    return {
      openTickets: tickets.filter(isTicketOpen),
      settledTickets: tickets.filter(notPred(isTicketOpen))
    };
  }, [tickets]);

  useEffect(() => {
    if (!loadingServices) {
      const abortController = new AbortController();
      updateOpenTickets(abortController);
      return () => {
        abortController.abort();
      };
    }
    return () => {};
  }, [loadingServices]);

  async function updateOpenTickets(abortController: AbortController) {
    try {
      if (loadingServices) {
        return;
      }

      const retailTicketIds = ticketStore
        .getAll()
        .filter(isTicketOpen)
        .map(t => t.retailTicketId);
      await queryAndUpdateTickets({
        retailTicketIds,
        abortController,
        dynamicConfig,
        americanWageringGateway
      });
      updateTickets(ticketStore.getAll());
    } catch (error) {
      logger.error(`Failure during updating tickets: ${error.message}`, error);
    }
  }

  function clearAll() {
    ticketStore.removeAll();
    updateTickets(ticketStore.getAll());
  }

  return {
    tickets,
    ...filteredTickets,
    clearAll
  };

  function updateTickets(tickets: TicketView[]) {
    setTickets(tickets.sort(compareTickets));
  }
}

export function isTicketOpen(ticket: TicketView) {
  return ticket.betStatus === BetStatusType.Ungraded || ticket.betStatus === BetStatusType.Unknown;
}

function notPred<T>(pred: (item: T) => boolean) {
  return function (item: T) {
    return !pred(item);
  };
}

function compareTickets(t1: TicketView, t2: TicketView) {
  if (t1.scannedAt === t2.scannedAt) {
    return 0;
  }
  if (t1.scannedAt < t2.scannedAt) {
    return 1;
  }
  return -1;
}
